"use client";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { ApiError } from "@/lib/api";
import {
  getSupplierPortalContext,
  getSupplierSession,
  logoutSupplierSession,
  type SupplierPortalContext,
} from "@/lib/api/suppliers";
import { mapRequestOutcome, isCancelledRequest } from "@/lib/commands/outcome";
import { clearEventOperations } from "@/lib/commands/event-operation-store";
import { clearBidOperations } from "@/lib/commands/bid-operation-store";

export type SupplierPortalStatus =
  | "loading"
  | "ready"
  | "unauthorized"
  | "forbidden"
  | "signedOut"
  | "notFound"
  | "unavailable";
interface Value {
  context?: SupplierPortalContext;
  status: SupplierPortalStatus;
  reload: () => void;
  signOut: () => Promise<void>;
  signOutPending: boolean;
  invalidate: (error: unknown) => void;
  ensureBinding: (signal: AbortSignal) => Promise<boolean>;
}
const Context = createContext<Value | null>(null);
type SupplierBinding = Pick<
  SupplierPortalContext,
  "tenantId" | "supplierId" | "accessId" | "accountId"
>;
function sameBinding(first: SupplierBinding, second: SupplierBinding) {
  return (
    first.tenantId === second.tenantId &&
    first.supplierId === second.supplierId &&
    first.accessId === second.accessId &&
    first.accountId === second.accountId
  );
}
function errorStatus(error: unknown): SupplierPortalStatus {
  const outcome = mapRequestOutcome(error);
  return outcome === "unauthorized" ||
    outcome === "forbidden" ||
    outcome === "notFound"
    ? outcome
    : "unavailable";
}
async function load(signal: AbortSignal) {
  const session = (await getSupplierSession({ signal })).data;
  const context = (
    await getSupplierPortalContext({
      tenantId: session.tenantId,
      supplierId: session.supplierId,
      signal,
    })
  ).data;
  if (!sameBinding(session, context))
    throw new ApiError("Supplier binding changed", {
      kind: "forbidden",
      code: "supplier_context_changed",
    });
  return context;
}
function clearRecovery(context?: SupplierPortalContext) {
  if (!context) return;
  try {
    const binding = {
      audience: "supplier" as const,
      tenantId: context.tenantId,
      accountId: context.accountId,
      supplierId: context.supplierId,
      accessId: context.accessId,
    };
    clearEventOperations(window.localStorage, binding);
    clearBidOperations(window.localStorage, binding);
  } catch {}
}

export function SupplierPortalProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<Pick<Value, "context" | "status">>({
    status: "loading",
  });
  const [generation, setGeneration] = useState(0);
  const [signOutPending, setSignOutPending] = useState(false);
  const current = useRef<SupplierPortalContext | undefined>(undefined);
  const epoch = useRef(0);
  const contextLoad = useRef<AbortController | null>(null);
  const command = useRef<AbortController | null>(null);
  const mounted = useRef(false);
  const invalidate = useCallback((error: unknown) => {
    const status = errorStatus(error);
    epoch.current += 1;
    contextLoad.current?.abort();
    command.current?.abort();
    if (status !== "unavailable") {
      clearRecovery(current.current);
      current.current = undefined;
    }
    // Hide protected views on an uncertain logout; retain recovery until authority is known.
    setState({ status });
  }, []);
  const ensureBinding = useCallback(
    async (signal: AbortSignal) => {
      const expected = current.current;
      const generation = epoch.current;
      if (!expected) return false;
      try {
        const actual = (await getSupplierSession({ signal })).data;
        if (signal.aborted || generation !== epoch.current) return false;
        if (!sameBinding(actual, expected))
          throw new ApiError("Supplier binding changed", {
            kind: "forbidden",
            code: "supplier_context_changed",
          });
        return true;
      } catch (error) {
        if (
          !signal.aborted &&
          generation === epoch.current &&
          ["unauthorized", "forbidden", "notFound"].includes(
            mapRequestOutcome(error),
          )
        )
          invalidate(error);
        throw error;
      }
    },
    [invalidate],
  );
  useEffect(() => {
    const controller = new AbortController();
    contextLoad.current = controller;
    const token = epoch.current;
    void load(controller.signal).then(
      (context) => {
        if (controller.signal.aborted || token !== epoch.current) return;
        if (current.current && !sameBinding(current.current, context))
          clearRecovery(current.current);
        current.current = context;
        setState({ status: "ready", context });
      },
      (error) => {
        if (
          controller.signal.aborted ||
          token !== epoch.current ||
          isCancelledRequest(error)
        )
          return;
        const status = errorStatus(error);
        if (status !== "unavailable") {
          clearRecovery(current.current);
          current.current = undefined;
        }
        setState({ status });
      },
    );
    return () => controller.abort();
  }, [generation]);
  useEffect(() => {
    mounted.current = true;
    let probe: AbortController | undefined;
    const verify = () => {
      if (document.visibilityState !== "visible" || !current.current) return;
      probe?.abort();
      probe = new AbortController();
      void ensureBinding(probe.signal).catch(() => undefined);
    };
    window.addEventListener("focus", verify);
    document.addEventListener("visibilitychange", verify);
    return () => {
      mounted.current = false;
      command.current?.abort();
      probe?.abort();
      window.removeEventListener("focus", verify);
      document.removeEventListener("visibilitychange", verify);
    };
  }, [ensureBinding]);
  async function signOut() {
    if (!current.current || command.current) return;
    command.current = new AbortController();
    setSignOutPending(true);
    try {
      await logoutSupplierSession(
        current.current.csrfRequestToken,
        command.current.signal,
      );
      epoch.current += 1;
      contextLoad.current?.abort();
      clearRecovery(current.current);
      current.current = undefined;
      if (mounted.current) setState({ status: "signedOut" });
    } catch (error) {
      if (!isCancelledRequest(error) && mounted.current) invalidate(error);
    } finally {
      command.current = null;
      if (mounted.current) setSignOutPending(false);
    }
  }
  return (
    <Context.Provider
      value={{
        ...state,
        invalidate,
        ensureBinding,
        signOut,
        signOutPending,
        reload: () => {
          epoch.current += 1;
          contextLoad.current?.abort();
          setState({ status: "loading" });
          setGeneration((value) => value + 1);
        },
      }}
    >
      {children}
    </Context.Provider>
  );
}
export function useSupplierPortal() {
  const value = useContext(Context);
  if (!value) throw new Error("Supplier portal context is unavailable");
  return value;
}
